
// Libraries
import { PropType, defineComponent } from 'vue';

// Components
import OtpInput from 'client/components/TotpInput.vue';

// Constants and types
import {
  SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
  SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
  createSmsOtpLoginSession,
} from 'client/util/Constants/smsOtpLoginSession';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import RequestError from 'client/services/response/RequestError';

// Utils
import userLoginService from 'client/services/userLoginService';

import type {
  SmsOtpLoginSession,
  SmsOtpLoginSessionPatch,
} from 'client/util/Constants/smsOtpLoginSession';

const SMS_OTP_RESEND_COOLDOWN_SECONDS = 60;
/** 2 min with no successful verify — from send success or revisit (not tied to resend cooldown). */
const SMS_OTP_NO_VERIFY_FAIL_MS = 120_000;

/** Max completed successful SMS sends allowed per MFA challenge (initial + one resend). */
const SMS_OTP_MAX_SUCCESSFUL_SENDS = 2;

export default defineComponent({
  name: 'OtpUserLoginHandler',

  components: {
    OtpInput,
  },

  props: {
    smsLoginSession: {
      type: Object as PropType<SmsOtpLoginSession>,
      default: () => createSmsOtpLoginSession(),
    },
  },

  setup() {
    const uiLabel = useMfaLabels();
    return { uiLabel };
  },

  emits: ['error', 'success', 'sms-session-patch'],

  data() {
    return {
      otp: '',
      hasAlreadySubmitted: false,
      isVerifying: false,
      isSendingOtp: false,
      /** Local spinner until first inbound send finishes (skipped on revisit). */
      firstSendFinished: false,
      sessionId: '',
      verifySucceeded: false,
      /** After terminal timeout, OTP row is unmounted. */
      otpEntryHidden: false,
      resendCooldownSeconds: 0,
      cooldownTimerId: null as ReturnType<typeof setInterval> | null,
      resendConsumed: false,
      /** Terminal fired during verify; replay after verify settles if login did not succeed. */
      pendingTerminalAfterVerify: false,
      idleNoVerifyTimerId: null as ReturnType<typeof setTimeout> | null,
      childInterface: {
        clearInput: () => {},
        focusInput: () => {},
      },
    };
  },

  computed: {
    otpInputLoading(): boolean {
      return this.isVerifying || (this.isSendingOtp && !this.firstSendFinished);
    },

    resendDisabled(): boolean {
      return (
        this.verifySucceeded
        || this.isSendingOtp
        || this.resendConsumed
        || this.resendCooldownSeconds > 0
        || this.smsLoginSession.successfulSendCount >= SMS_OTP_MAX_SUCCESSFUL_SENDS
      );
    },

    resendButtonLabel(): string {
      if (this.resendCooldownSeconds > 0) {
        return `${this.uiLabel.sms_otp.resendLink} (${this.resendCooldownSeconds})`;
      }
      return this.uiLabel.sms_otp.resendLink;
    },

    hasResendsRemainingInSession(): boolean {
      return this.smsLoginSession.successfulSendCount < SMS_OTP_MAX_SUCCESSFUL_SENDS;
    },

    /** Keep resend beside label until OTP dismissed; use `resendDisabled` when exhausted. */
    showResendPrompt(): boolean {
      return !this.otpEntryHidden && !this.smsLoginSession.removedFromChooser;
    },
  },

  watch: {
    'smsLoginSession.successfulSendCount': function resendCountWatcher(next: number): void {
      if (next >= SMS_OTP_MAX_SUCCESSFUL_SENDS) {
        this.clearCooldownTimer();
        this.resendCooldownSeconds = 0;
      }
    },
  },

  mounted(): void {
    if (this.smsLoginSession.removedFromChooser) {
      return;
    }

    const cachedSid = this.smsLoginSession.lastSessionId || '';
    if (cachedSid) {
      this.sessionId = cachedSid;
    }

    if (this.smsLoginSession.initialSendEverAttempted) {
      this.firstSendFinished = true;
      if (this.hasResendsRemainingInSession) {
        this.startResendCooldown();
      }
      this.scheduleIdleNoVerifyWindow();
      return;
    }

    if (this.hasResendsRemainingInSession) {
      this.startResendCooldown();
    }

    (async (): Promise<void> => {
      await this.sendOtpInternal(false);
    })();
  },

  beforeDestroy() {
    this.clearAllSessionTimers();
  },

  methods: {
    emitSessionPatch(patch: SmsOtpLoginSessionPatch) {
      this.$emit('sms-session-patch', patch);
    },

    clearCooldownTimer() {
      if (this.cooldownTimerId != null) {
        clearInterval(this.cooldownTimerId);
        this.cooldownTimerId = null;
      }
    },

    startResendCooldown() {
      this.clearCooldownTimer();
      this.resendCooldownSeconds = SMS_OTP_RESEND_COOLDOWN_SECONDS;
      this.cooldownTimerId = setInterval(() => {
        if (this.resendCooldownSeconds <= 1) {
          this.resendCooldownSeconds = 0;
          this.clearCooldownTimer();
        } else {
          this.resendCooldownSeconds -= 1;
        }
      }, 1000);
    },

    scheduleIdleNoVerifyWindow() {
      if (this.verifySucceeded || this.smsLoginSession.removedFromChooser) {
        return;
      }
      this.clearTimeoutId(this.idleNoVerifyTimerId);
      this.idleNoVerifyTimerId = setTimeout(() => {
        this.idleNoVerifyTimerId = null;
        this.emitTerminalTimeout();
      }, SMS_OTP_NO_VERIFY_FAIL_MS);
    },

    clearFailureTimers() {
      this.clearTimeoutId(this.idleNoVerifyTimerId);
      this.idleNoVerifyTimerId = null;
    },

    clearTimeoutId(id: ReturnType<typeof setTimeout> | null) {
      if (id != null) {
        clearTimeout(id);
      }
    },

    clearAllSessionTimers() {
      this.clearCooldownTimer();
      this.clearFailureTimers();
    },

    emitTerminalTimeout() {
      if (this.verifySucceeded) {
        return;
      }
      if (this.isVerifying) {
        this.pendingTerminalAfterVerify = true;
        return;
      }
      this.pendingTerminalAfterVerify = false;
      this.otpEntryHidden = true;
      this.clearAllSessionTimers();
      this.$emit(
        'error',
        new RequestError(
          500,
          this.uiLabel.sms_otp.terminalNoInput,
          SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
        ),
      );
    },

    onResendSms() {
      if (
        this.resendConsumed
        || this.verifySucceeded
        || this.isSendingOtp
        || this.resendCooldownSeconds > 0
        || this.smsLoginSession.successfulSendCount >= SMS_OTP_MAX_SUCCESSFUL_SENDS
      ) {
        return;
      }
      this.clearTimeoutId(this.idleNoVerifyTimerId);
      this.idleNoVerifyTimerId = null;
      this.sendOtpInternal(true);
    },

    async sendOtpInternal(isResend: boolean) {
      if (this.verifySucceeded) {
        return;
      }

      if (isResend) {
        this.resendConsumed = true;
      }

      const isInboundInitialSend = !isResend;
      const completesInitialScreenLoadAttempt = isInboundInitialSend
        && !this.smsLoginSession.initialSendEverAttempted;

      this.isSendingOtp = true;

      try {
        const response = await userLoginService.sendOtp() as { data?: { sessionId?: string } };
        const sid = response.data?.sessionId || '';
        this.sessionId = sid;

        this.emitSessionPatch({
          successfulSendIncrement: 1,
          lastSessionId: sid,
        });

        this.scheduleIdleNoVerifyWindow();
      } catch (error) {
        if (isResend) {
          this.scheduleIdleNoVerifyWindow();
        }
        this.$emit('error', error);
      } finally {
        this.isSendingOtp = false;
        if (completesInitialScreenLoadAttempt) {
          this.firstSendFinished = true;
          this.emitSessionPatch({ initialSendEverAttempted: true });
        }
      }
    },

    clearInput() {
      this.childInterface.clearInput();
    },

    focusInput() {
      this.childInterface.focusInput();
    },

    getChildInterface(childInterface: { clearInput: () => void, focusInput: () => void }) {
      this.childInterface = childInterface;
    },

    async submit() {
      if (this.otpEntryHidden) {
        return;
      }
      this.clearFailureTimers();
      this.isVerifying = true;
      try {
        await userLoginService.verifyOtp(this.otp, this.sessionId);
        this.verifySucceeded = true;
        this.pendingTerminalAfterVerify = false;
        this.clearAllSessionTimers();
        this.$emit('success');
      } catch (error) {
        this.hasAlreadySubmitted = true;
        if (
          error instanceof RequestError
          && error.statusCode === SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE
        ) {
          this.otpEntryHidden = true;
          this.clearAllSessionTimers();
        } else {
          this.clearInput();
          this.focusInput();
        }
        this.$emit('error', error);
      } finally {
        this.isVerifying = false;
        if (this.pendingTerminalAfterVerify && !this.verifySucceeded) {
          this.pendingTerminalAfterVerify = false;
          this.emitTerminalTimeout();
        }
      }
    },
  },
});
